import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import EnvironmentSwitcher from './EnvironmentSwitcher';
import { ReactComponent as ActionItemsIcon } from '../assets/action-items.svg';

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { t } = useI18n();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Initialize sidebar state from localStorage or default to false (expanded)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });

  // Update localStorage whenever sidebar state changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 h-16 bg-white shadow-md z-10">
        <div className="flex items-center justify-between h-full px-4">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-[#075e54]">Nexi Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4 relative">
            {/* Environment Switcher */}
            <EnvironmentSwitcher />

            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 focus:outline-none"
              >
                <div className="w-8 h-8 rounded-full bg-[#075e54] flex items-center justify-center text-white">
                  {currentUser?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="text-gray-700">{currentUser?.username || 'User'}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b">
                    <p className="font-medium">{currentUser?.username}</p>
                    <p className="text-gray-500 text-xs truncate">{currentUser?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex pt-16">
        {/* Sidebar */}
        <div
          className={`${sidebarCollapsed ? 'w-20' : 'w-64'} h-[calc(100vh-4rem)] bg-white shadow-md fixed transition-all duration-300`}
        >
          {/* Logo and Toggle Button */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : ''}`}>
              {/* Logo SVG */}
              <div className="w-10 h-10 rounded-md bg-[#075e54] flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              {!sidebarCollapsed && <span className="ml-2 font-bold text-[#075e54]">Nexi</span>}
            </div>
            <button
              onClick={toggleSidebar}
              className={`text-gray-500 hover:text-gray-700 focus:outline-none ${sidebarCollapsed ? 'hidden' : ''}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M15.707 4.293a1 1 0 010 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Sidebar Toggle Button (only visible when collapsed) */}
          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="w-full flex justify-center py-2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          )}

          <div className="p-4">
            <nav className="space-y-2">
              <Link
                to="/"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/' && !location.pathname.includes('/clinics') && !location.pathname.includes('/dashboard') ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Dashboard</span>}
              </Link>
              <Link
                to="/clinics"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname.includes('/clinics') ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Clinic Configurations</span>}
              </Link>
              <Link
                to="/action-items"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/action-items' ? 'bg-gray-100' : ''
                }`}
              >
                <ActionItemsIcon className="h-5 w-5" />
                {!sidebarCollapsed && <span className="ml-2">Action Items</span>}
              </Link>
              <Link
                to="/onboarding"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/onboarding' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Onboarding</span>}
              </Link>
              <Link
                to="/onboarding-tokens"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/onboarding-tokens' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-2.21 0-4 1.12-4 2.5S9.79 13 12 13s4-1.12 4-2.5S14.21 8 12 8zm0 0V6m0 7v2m-7-4h2m10 0h2m-2.5 6.5l-1.5-1.5m-6 0l-1.5 1.5m0-11l1.5 1.5m6 0l1.5-1.5" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Onboarding Tokens</span>}
              </Link>
              <Link
                to="/settings"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/settings' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Settings</span>}
              </Link>
              <Link
                to="/toolbox"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/toolbox' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.867 19.125h.008v.008h-.008v-.008z" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Tool Box</span>}
              </Link>
              <Link
                to="/clinic-integration-events"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/clinic-integration-events' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Clinic Integration Events</span>}
              </Link>
              <Link
                to="/meta-whatsapp-events"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/meta-whatsapp-events' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">Meta WhatsApp Events</span>}
              </Link>
              <Link
                to="/phone-search"
                className={`flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded ${
                  location.pathname === '/phone-search' ? 'bg-gray-100' : ''
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {!sidebarCollapsed && <span className="ml-2">{t('phoneSearch.navLabel')}</span>}
              </Link>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className={`${sidebarCollapsed ? 'ml-20' : 'ml-64'} flex-1 p-3 transition-all duration-300`}>
          <div className="grid grid-cols-1 gap-3">
            <div className="bg-grey rounded-lg">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
